import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PartOne from './PartOne';
import PartTwo from './PartTwo';
import PartThree from './PartThree';
import PartFour from './PartFour';
import PartFive from './PartFive';
import CertificationSuccess from '../ComponentForms/CertificationSuccess';
import ErrorMessage from '../../../components/ErrorMessage';
import LoadingSpinner from '../../../components/LoadingSpinner';
import OrganisationLogo from '../../../components/OrganisationLogo';

// ICONS
import { ReactComponent as LeftArrrow } from '../../../assets/svg/arrow-left.svg';

// TYPES
import {
	AccountExistingCertificationsCertification,
	AccountJoinOrganisationCertification,
	AccountJoinOrganisationCommand,
	DomainCertificationReviewType,
	DomainCertificationType,
	DomainProfileType,
	OrganisationFindOrganisationSummary
} from '../../../api/models';
import { FileType } from '../../../components/FileUpload';

export interface CertificationTypes {
	organisationRole?: string;
	authorityToWork?: {
		certificationType?: string;
		state?: string;
		expiry?: string;
		certificationNumber?: string;
		createAttachments?: string[];
		attachments?: FileType[];
		userAcknowledgement?: boolean;
	}[];
	policeCheck?: {
		certificationType?: string;
		date?: string;
		createAttachments?: string[];
		attachments?: FileType[];
	};
	qualificationCertificate?: {
		qualificationCertificateType?: string;
		title?: string;
		date?: string;
		organisation?: string;
		createAttachments?: string[];
		attachments?: FileType[];
	}[];
	vaccinationCertificate?: {
		createAttachments?: string[];
		attachments?: FileType[];
		userAcknowledgement?: boolean;
	};
	acknowledge?: boolean;
}

interface CertificationStaffTypes {
	organisationData?: OrganisationFindOrganisationSummary;
	profileData?: { label?: string; value?: string } | undefined;
}

const CertificationStaff: React.FC<CertificationStaffTypes> = ({ organisationData, profileData }) => {
	// STATES
	const [certificationFormData, setCertificationFormData] = React.useState<CertificationTypes>({});

	const [formSection, setFormSection] = React.useState<string>('1');

	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [existingCertifications, setExistingCertifications] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const [existingTeacherRegistration, setExistingTeacherRegistration] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const [existingWWCCRegistration, setExistingWWCCRegistration] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const [newlyCreatedOrganisationId, setNewlyCreatedOrganisationId] = React.useState<string>('');
	const [isProfileActive, setIsProfileActive] = React.useState<boolean>(false);

	const getExistingCertifications = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountExistingCertifications();

		if (response.isError) {
			setIsLoading(false);
		} else {
			setExistingCertifications(response.result);

			setExistingTeacherRegistration(
				response.result &&
					response.result.filter(
						(item: AccountExistingCertificationsCertification) =>
							item.certificationType === DomainCertificationType.TeacherRegistration
					)
			);

			setExistingWWCCRegistration(
				response.result &&
					response.result.filter(
						(item: AccountExistingCertificationsCertification) =>
							item.certificationType === DomainCertificationType.WorkingWithChildrenCheck
					)
			);

			setIsLoading(false);

			setShowError(false);
		}
	};

	React.useEffect(() => {
		getExistingCertifications();
	}, []);

	// REFS
	const normalisedCertificationData = React.useRef<any[]>([]);

	// Go back to previous form section
	const goBack = () => {
		switch (formSection!) {
			case '2':
				setFormSection('1');
				break;
			case '3':
				setFormSection('2');
				break;
			case '4':
				setFormSection('3');
				break;
			case '5':
				setFormSection('4');
				break;
			default:
				break;
		}
	};

	const formatCertificationData = (certificationFormData: CertificationTypes) => {
		const authorityToWork: AccountJoinOrganisationCertification[] =
			certificationFormData && certificationFormData.authorityToWork
				? certificationFormData.authorityToWork.map((form: any) => {
						return form.certificationType !== ''
							? {
									certificationType: form.certificationType,
									state: form.state,
									expiry: form.expiry,
									certificationNumber: form.certificationNumber,
									createAttachments: form.createAttachments,
									userAcknowledgement: form.userAcknowledgement
							  }
							: {};
				  })
				: [];

		const policeCheck: AccountJoinOrganisationCertification =
			certificationFormData.policeCheck && certificationFormData.policeCheck.date
				? {
						certificationType: DomainCertificationType.PoliceCheck,
						date: certificationFormData.policeCheck && certificationFormData.policeCheck.date,
						createAttachments: certificationFormData.policeCheck && certificationFormData.policeCheck.createAttachments
				  }
				: {};

		const qualificationCertificate: AccountJoinOrganisationCertification[] =
			certificationFormData && certificationFormData.qualificationCertificate
				? certificationFormData.qualificationCertificate.map((form: any) => {
						return form.qualificationCertificateType
							? {
									certificationType: form.qualificationCertificateType,
									title: form.title,
									date: form.date,
									organisation: form.organisation,
									createAttachments: form.createAttachments
							  }
							: {};
				  })
				: [];

		const vaccinationCertificate: AccountJoinOrganisationCertification =
			certificationFormData.vaccinationCertificate?.createAttachments &&
			certificationFormData.vaccinationCertificate?.createAttachments.length > 0
				? {
						certificationType: DomainCertificationType.VaccinationCertificate,
						createAttachments: certificationFormData.vaccinationCertificate?.createAttachments,
						userAcknowledgement: certificationFormData.vaccinationCertificate?.userAcknowledgement
				  }
				: {};

		normalisedCertificationData.current = [
			...authorityToWork,
			policeCheck,
			...qualificationCertificate,
			vaccinationCertificate
		]; // all the normalized certification data
	};

	const doJoin = async () => {
		formatCertificationData(certificationFormData);

		const noneEmptyData: AccountJoinOrganisationCertification[] = [];

		normalisedCertificationData.current.map((item) => Object.keys(item).length > 0 && noneEmptyData.push(item));

		const fields: AccountJoinOrganisationCommand = {
			organisationIdentifier: organisationData?.identifier,
			profileType: profileData && (profileData.value as DomainProfileType),
			staffType: certificationFormData.organisationRole,
			certifications: noneEmptyData
		};

		setIsLoading(true);

		const response = await apibridge.doJoinOrganisation(fields);

		if (response.isError) {
			setIsLoading(false);

			setShowError(true);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setShowError(false);

			setIsLoading(false);

			setNewlyCreatedOrganisationId(response.result.profileId);

			setIsProfileActive(response.result.isActive);

			setFormSection('success');
		}
	};

	return (
		<div className="page-add-certification">
			{showError ? (
				<ErrorMessage message={formErrorMessage} />
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="header row">
									<div className="col-2">
										{formSection !== '1' && formSection !== 'success' ? (
											<button onClick={goBack} className="back-button">
												<LeftArrrow />
												Back
											</button>
										) : null}
									</div>

									<div className="section-header col-8">
										{formSection === 'success' ? null : (
											<>
												<OrganisationLogo src={organisationData && organisationData.image} />

												<h2 className="h1">{organisationData?.name}</h2>

												<p className="sub-grey-text">Please provide additional certification information.</p>

												<p className="sub-grey-text">Part {formSection} of 5</p>
											</>
										)}
									</div>

									<div className="col-2"></div>
								</div>

								<section>
									{formSection === '1' ? (
										<PartOne
											staffTypes={organisationData && organisationData.staffTypes}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '2' ? (
										<PartTwo
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(item) => item.certificationType === DomainCertificationReviewType.WorkingWithChildrenCheck
												)
											}
											existingWWCCRegistration={existingWWCCRegistration}
											existingTeacherRegistration={existingTeacherRegistration}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '3' ? (
										<PartThree
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(cert) => cert.reviewType === DomainCertificationReviewType.PoliceCheck
												)
											}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '4' ? (
										<PartFour
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(cert) => cert.reviewType === DomainCertificationReviewType.Qualification
												)
											}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '5' ? (
										<PartFive
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(cert) => cert.reviewType === DomainCertificationReviewType.Vaccination
												)
											}
											certificationData={certificationFormData}
											setCertificationFormData={setCertificationFormData}
											loading={isLoading}
											formErrorMessage={formErrorMessage}
											setFormSection={setFormSection}
											doJoin={doJoin}
										/>
									) : formSection === 'success' ? (
										<CertificationSuccess
											organisationName={organisationData?.name}
											isActive={isProfileActive}
											newlyCreatedOrganisationId={newlyCreatedOrganisationId}
										/>
									) : null}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CertificationStaff;
